import type { ReactElement } from 'react'
import { SecondaryButton } from './SecondaryButton'
import { defaultRouteCardCopy, type RouteCardCopySet } from './copy'
import styles from './RouteError.module.css'

/**
 * Props for {@link RouteError}.
 */
export interface RouteErrorProps {
  /**
   * Subtitle message shown under the title. Defaults to the localised
   * network-failure copy when omitted.
   */
  readonly message?: string
  /** Tap retry handler. If omitted, no Retry button is rendered. */
  readonly onRetry?: () => void
  /** Localised copy. @defaultValue {@link defaultRouteCardCopy} */
  readonly copy?: RouteCardCopySet
}

/**
 * Error state for the route-built panel (Figma spec §2.9). Shown when the
 * routing algorithm threw or the network request failed.
 *
 * Same shape as {@link RouteEmpty}: a centered Message block (title
 * `semiboldPx14` "Couldn't build route" / subtitle `regularPx12`) over a
 * `Route details row` carrying the shared {@link SecondaryButton} labelled
 * "Retry" (h 64, r 32). The button emits `retryTapped` via `onRetry`.
 *
 * `message` lets the caller surface a specific failure without re-skinning.
 */
export function RouteError({
  message,
  onRetry,
  copy = defaultRouteCardCopy,
}: RouteErrorProps): ReactElement {
  const subtitle = message ?? copy.errorNetworkMessage
  return (
    <div className={styles.root} data-testid="route-error" role="alert">
      <div className={styles.message}>
        <p className={styles.title}>{copy.errorTitle}</p>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>
      {onRetry !== undefined && (
        <div className={styles.detailsRow}>
          <SecondaryButton label={copy.retry} onClick={onRetry} testId="route-error-retry" />
        </div>
      )}
    </div>
  )
}
